import { useState, useEffect, useRef } from 'react';
import { apiFetch } from '../dppApi';
import '../App.css';

// The executor's view of an estate share (role='executor'). The backend reveals
// NOTHING (locked:true) until a death certificate is uploaded; then the estate
// unlocks and the executor may exercise the will — minting one inheritance claim
// link per designated piece that each beneficiary signs in to claim, exactly like
// an initial sale. Three states: locked (upload cert) → ready (review + exercise)
// → exercised (claim links sent).
const ExecutorEstate = ({ token }) => {
    const base = `/api/user/estate/shared/${token}`;
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [certBusy, setCertBusy] = useState(false);
    const certRef = useRef(null);
    const [confirmed, setConfirmed] = useState(false);
    const [exBusy, setExBusy] = useState(false);
    const [result, setResult] = useState(null);

    useEffect(() => {
        let alive = true;
        apiFetch(base)
            .then(d => { if (alive) setData(d); })
            .catch(e => { if (alive) setError(e.message); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [base]);

    const uploadCert = async (file) => {
        if (!file) return;
        setCertBusy(true);
        setError(null);
        try {
            const form = new FormData();
            form.append('document', file);
            await apiFetch(`${base}/death-cert`, { method: 'POST', body: form, isForm: true });
            const d = await apiFetch(base);   // re-fetch: the estate is now unlocked
            setData(d);
        } catch (e) {
            setError(e.message);
        } finally {
            setCertBusy(false);
            if (certRef.current) certRef.current.value = '';
        }
    };

    const exercise = async () => {
        setExBusy(true);
        setError(null);
        try {
            const res = await apiFetch(`${base}/exercise`, { method: 'POST' });
            setResult(res);
        } catch (e) {
            setError(e.message);
        } finally {
            setExBusy(false);
        }
    };

    if (loading) return <p className="collection-status collection-status--flush">Loading…</p>;
    if (error && !data) return <p className="dpp-error">{error}</p>;

    const designated = (data?.pieces || []).filter(p => p.designation);
    const thumbStyle = (url) =>
        url && url.startsWith('http') ? { backgroundImage: `url(${url})`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined;

    return (
        <div className="settings-section">
            <div className="settings-section-head">
                <div>
                    <h2 className="settings-section-title">Exercise the will</h2>
                    <p className="settings-section-sub">{data?.owner_name ? `Estate of ${data.owner_name}` : 'Estate'}</p>
                </div>
            </div>
            {error && <p className="dpp-error">{error}</p>}

            {/* SUCCESS */}
            {result && (
                <div className="exercise-panel">
                    <p className="exercise-success-head">Will exercised</p>
                    <p className="settings-section-sub" style={{ marginTop: 0 }}>
                        {result.exercised} {result.exercised === 1 ? 'piece' : 'pieces'} transferred. Each beneficiary has been emailed a link to sign in and claim.
                    </p>
                    {(result.pieces || []).map((r, i) => (
                        <div className="exercise-row" key={i}>
                            <span className="exercise-thumb" aria-hidden="true" />
                            <span className="exercise-piece">{r.product_name}</span>
                            <span className="exercise-right">
                                <span className="exercise-to"><b>{r.beneficiary_name || r.beneficiary_email}</b></span>
                                <span className="exercise-status">{r.emailed ? 'Claim link emailed · pending claim' : 'Claim link ready · pending claim'}</span>
                            </span>
                        </div>
                    ))}
                </div>
            )}

            {/* LOCKED — needs the death certificate */}
            {!result && data?.locked && (
                <div className="exercise-panel">
                    <p className="settings-section-sub" style={{ marginTop: 0 }}>
                        As executor, you can transfer each designated piece to its beneficiary. This is permitted once you upload the death certificate.
                    </p>
                    <div className="cert-upload">
                        <div className="cert-upload-title">Upload the death certificate</div>
                        <div className="cert-upload-note">PDF, JPG or PNG. Stored privately as proof of exercise.</div>
                        <button className="catalogue-btn-primary" disabled={certBusy} onClick={() => certRef.current?.click()}>
                            {certBusy ? 'Uploading…' : 'Choose file…'}
                        </button>
                        <input ref={certRef} type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }}
                            onChange={e => uploadCert(e.target.files[0] || null)} />
                    </div>
                </div>
            )}

            {/* READY — cert uploaded, not yet exercised */}
            {!result && !data?.locked && (
                <div className="exercise-panel">
                    {data?.death_cert && (
                        <div className="cert-chip">
                            <span>Death certificate:&nbsp;
                                {data.death_cert.url
                                    ? <a href={data.death_cert.url} target="_blank" rel="noreferrer">{data.death_cert.filename || 'certificate'}</a>
                                    : (data.death_cert.filename || 'uploaded')}
                            </span>
                            <button className="estate-inline-btn" disabled={certBusy} onClick={() => certRef.current?.click()}>Replace</button>
                            <input ref={certRef} type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }}
                                onChange={e => uploadCert(e.target.files[0] || null)} />
                        </div>
                    )}

                    {designated.length === 0 ? (
                        <p className="collection-status collection-status--flush section-empty">No designated pieces to exercise.</p>
                    ) : (
                        <>
                            <p className="settings-section-sub">
                                {designated.length} designated {designated.length === 1 ? 'piece' : 'pieces'} will transfer to their beneficiaries. Each beneficiary is emailed a link to sign in and claim.
                            </p>
                            {designated.map(p => (
                                <div className="exercise-row" key={p.product_id}>
                                    <span className="exercise-thumb" aria-hidden="true" style={thumbStyle(p.image_url)} />
                                    <span className="exercise-piece">{p.name}</span>
                                    <span className="exercise-right">
                                        <span className="exercise-to">to <b>{p.designation.beneficiary_name || p.designation.beneficiary_email}</b></span>
                                        {p.designation.beneficiary_email && <span className="exercise-mail">{p.designation.beneficiary_email}</span>}
                                    </span>
                                </div>
                            ))}
                            <div className="exercise-warn">
                                Exercising the will is irreversible. Ownership of each piece moves to the beneficiary once they claim it, and the brand records an executed inheritance.
                            </div>
                            <label className="exercise-confirm">
                                <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} />
                                I confirm the owner has passed and I am authorised to exercise this will.
                            </label>
                            <div style={{ marginTop: 14 }}>
                                <button className="exercise-btn" disabled={!confirmed || exBusy} onClick={exercise}>
                                    {exBusy ? 'Exercising…' : `Exercise will · ${designated.length} ${designated.length === 1 ? 'piece' : 'pieces'}`}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default ExecutorEstate;
